import React, { useState } from 'react';
import { getAuthToken } from '../utils/authStorage';
import './AdatModositas.css';

const AdatModositas = () => {
    const [activeModal, setActiveModal] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [notification, setNotification] = useState(null);

    const [showPeriodModal, setShowPeriodModal] = useState(false);
    const [periods, setPeriods] = useState([]);
    const [loadingPeriods, setLoadingPeriods] = useState(false);
    const [periodForm, setPeriodForm] = useState({
        name: '',
        start_date: '',
        end_date: '',
        is_active: false
    });
    const [editingPeriodName, setEditingPeriodName] = useState(null);
    const [savingPeriod, setSavingPeriod] = useState(false);
    const [deletingPeriodName, setDeletingPeriodName] = useState(null);
    const [formErrors, setFormErrors] = useState({});

    const tiles = [
        {
            id: 'szakadatok',
            title: 'Szakadatok',
            icon: 'school',
            color: '#4CAF50',
            gradient: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
        },
        {
            id: 'advisory-board',
            title: 'Advisory Board',
            icon: 'people',
            color: '#2196F3',
            gradient: 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)',
        },
        {
            id: 'super-adminok',
            title: 'Adminok',
            icon: 'rate_review',
            color: '#FF9800',
            gradient: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
        }
    ];

    const openModal = (modalId) => {
        setActiveModal(modalId);
        setSelectedFile(null);
    };

    const closeModal = () => {
        setActiveModal(null);
        setSelectedFile(null);
        setUploading(false);
    };

    const openPeriodModal = async () => {
        setShowPeriodModal(true);
        await loadPeriods();
    };

    const closePeriodModal = () => {
        setShowPeriodModal(false);
        resetPeriodForm();
    };

    const loadPeriods = async () => {
        setLoadingPeriods(true);
        try {
            const token = localStorage.getItem('access_token');
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/periods`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const result = await response.json();
                setPeriods(result.data || []);
            }
        } catch (error) {
            console.error('Error loading periods:', error);
            showNotification('Hiba az időszakok betöltésekor!', 'error');
        } finally {
            setLoadingPeriods(false);
        }
    };

    const validatePeriodForm = () => {
        const errors = {};
        
        if (!periodForm.name.trim()) {
            errors.name = 'A név kitöltése kötelező!';
        }
        
        if (!periodForm.start_date) {
            errors.start_date = 'A kezdő dátum kitöltése kötelező!';
        }
        
        if (!periodForm.end_date) {
            errors.end_date = 'A záró dátum kitöltése kötelező!';
        }
        
        if (periodForm.start_date && periodForm.end_date) {
            if (new Date(periodForm.end_date) < new Date(periodForm.start_date)) {
                errors.end_date = 'A záró dátum nem lehet korábbi a kezdő dátumnál!';
            }
        }
        
        // Check for overlapping periods
        if (periodForm.start_date && periodForm.end_date) {
            const hasOverlap = periods.some(p => {
                // Skip current period when editing
                if (editingPeriodName && p.name === editingPeriodName) return false;
                // Check overlap: new period overlaps with existing if:
                // new_start <= existing_end AND new_end >= existing_start
                return new Date(periodForm.start_date) <= new Date(p.end_date) && 
                       new Date(periodForm.end_date) >= new Date(p.start_date);
            });
            
            if (hasOverlap) {
                errors.end_date = 'Az időszak nem fedhet át egy meglévő időszakkal!';
            }
        }
        
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handlePeriodFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        setPeriodForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        // Clear error for this field when user starts typing
        if (formErrors[name]) {
            setFormErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const resetPeriodForm = () => {
        setPeriodForm({
            name: '',
            start_date: '',
            end_date: '',
            is_active: false
        });
        setEditingPeriodName(null);
        setFormErrors({});
    };

    const handleEditPeriod = (period) => {
        setEditingPeriodName(period.name);
        setPeriodForm({
            name: period.name,
            start_date: period.start_date ? period.start_date.split('T')[0] : '',
            end_date: period.end_date ? period.end_date.split('T')[0] : '',
            is_active: period.is_active
        });
        setFormErrors({});
    };

    const handleSavePeriod = async () => {
        if (!validatePeriodForm()) {
            return;
        }

        setSavingPeriod(true);
        try {
            const token = localStorage.getItem('access_token');
            const url = editingPeriodName 
                ? `${process.env.REACT_APP_API_BASE_URL}/api/periods/${encodeURIComponent(editingPeriodName)}`
                : `${process.env.REACT_APP_API_BASE_URL}/api/periods`;
            const method = editingPeriodName ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(periodForm)
            });

            const result = await response.json();

            if (!response.ok) {
                showNotification(result.error || 'Hiba a mentés során!', 'error', result);
                return;
            }

            showNotification(
                editingPeriodName ? 'Időszak sikeresen frissítve!' : 'Időszak sikeresen létrehozva!',
                'success'
            );
            resetPeriodForm();
            await loadPeriods();
        } catch (error) {
            console.error('Error saving period:', error);
            showNotification('Hiba a mentés során!', 'error');
        } finally {
            setSavingPeriod(false);
        }
    };

    const handleDeletePeriod = async (name) => {
        if (!window.confirm('Biztosan törölni szeretné ezt az időszakot?')) return;

        setDeletingPeriodName(name);
        try {
            const token = localStorage.getItem('access_token');
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/periods/${encodeURIComponent(name)}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            const result = await response.json();

            if (!response.ok) {
                showNotification(result.error || 'Hiba a törlés során!', 'error', result);
                return;
            }

            showNotification('Időszak sikeresen törölve!', 'success');
            await loadPeriods();
        } catch (error) {
            console.error('Error deleting period:', error);
            showNotification('Hiba a törlés során!', 'error');
        } finally {
            setDeletingPeriodName(null);
        }
    };

    const handleFileUpload = (event, type) => {
        const file = event.target.files[0];
        if (file) {
            // Validate file type
            if (!file.name.match(/\.(xlsx|xls)$/)) {
                showNotification(
                    'Érvénytelen fájltípus!', 
                    'error', 
                    'Csak Excel fájlokat (.xlsx, .xls) lehet feltölteni!'
                );
                return;
            }
            
            // Validate file size (10MB)
            if (file.size > 10 * 1024 * 1024) {
                showNotification(
                    'A fájl túl nagy!', 
                    'error', 
                    'A fájl mérete nem lehet nagyobb 10MB-nál!'
                );
                return;
            }
            
            setSelectedFile(file);
            console.log(`Selected ${type} file:`, file.name);
        }
    };

    const handleDeselectFile = () => {
        setSelectedFile(null);
        console.log('File deselected');
    };

    const handleUpload = async () => {
        if (!selectedFile || !activeModal) return;
        
        setUploading(true);
        
        try {
            const formData = new FormData();
            formData.append('excelFile', selectedFile);
            
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/upload/excel/${activeModal}`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${getAuthToken()}` },
                body: formData
            });
            
            const result = await response.json();
            
            if (!response.ok) {
                // Handle validation errors with detailed information
                const errorMessage = result.error || 'Upload failed';
                const details = result.validationDetails ? result : errorMessage;
                
                showNotification(
                    'Excel fájl feltöltése sikertelen!',
                    'error',
                    details
                );
                closeModal();
                return;
            }
            
            console.log('Upload successful:', result);
            
            // Show success message with file details
            const successMessage = `Excel fájl sikeresen feltöltve és feldolgozva!`;
            const successDetails = `Fájl: ${result.fileName}\n`;
            
            showNotification(
                successMessage,
                'success',
                successDetails
            );
            
            closeModal();
        } catch (error) {
            console.error('Upload failed:', error);
            showNotification(
                'Feltöltés sikertelen!',
                'error',
                `Hiba történt a feltöltés során: ${error.message}`
            );
            closeModal();
        } finally {
            setUploading(false);
        }
    };

    const formatPeriodDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('hu-HU');
    };

    // Notification functions
    const showNotification = (message, type = 'info', details = null) => {
        setNotification({ message, type, details, timestamp: Date.now() });
        
        // Auto-hide notification after 5 seconds for success messages
        if (type === 'success') {
            setTimeout(() => setNotification(null), 5000);
        }
    };

    const hideNotification = () => {
        setNotification(null);
    };

    const renderNotification = () => {
        if (!notification) return null;

        return (
            <div className={`notification notification-${notification.type}`}>
                <div className="notification-content">
                    <div className="notification-header">
                        <div className="notification-icon">
                            <i className="material-icons">
                                {notification.type === 'success' ? 'check_circle' : 
                                 notification.type === 'error' ? 'error' : 'info'}
                            </i>
                        </div>
                        <div className="notification-message">
                            {notification.message}
                        </div>
                        <button className="notification-close" onClick={hideNotification}>
                            <i className="material-icons">close</i>
                        </button>
                    </div>
                    
                    {notification.details && (
                        <div className="notification-details">
                            {typeof notification.details === 'string' ? (
                                <pre>{notification.details}</pre>
                            ) : (
                                <div>
                                    {notification.details.validationDetails && (
                                        <div className="validation-details">
                                            
                                            
                                            {notification.details.validationDetails.expectedHeaders && (
                                                <div className="expected-structure">
                                                    <div className="headers-comparison">
                                                        <div className="expected-headers">
                                                            <strong>Elvárt fájlstruktúra:</strong>
                                                            <ol>
                                                                {notification.details.validationDetails.expectedHeaders.map((header, index) => (
                                                                    <li key={index}>{header}</li>
                                                                ))}
                                                            </ol>
                                                        </div>
                                                        
                                                        {notification.details.validationDetails.actualHeaders && (
                                                            <div className="actual-headers">
                                                                <strong>Feltöltött fájl struktúrája:</strong>
                                                                <ol>
                                                                    {notification.details.validationDetails.actualHeaders.map((header, index) => (
                                                                        <li key={index}>{header || '(üres)'}</li>
                                                                    ))}
                                                                </ol>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderModal = () => {
        if (!activeModal) return null;

        const currentTile = tiles.find(tile => tile.id === activeModal);

        return (
            <div className="modal-overlay" onClick={closeModal}>
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-header" style={{ background: currentTile.gradient }}>
                        <div className="modal-title">
                            <i className="material-icons">{currentTile.icon}</i>
                            <div>
                                <h2>{currentTile.title}</h2>
                            </div>
                        </div>
                        <button className="modal-close-button" onClick={closeModal}>
                            <i className="material-icons">close</i>
                        </button>
                    </div>
                    
                    <div className="modal-body">
                        <div className="upload-section">
                            <div className="file-upload-area">
                                <div className="upload-zone">
                                    <i className="material-icons">cloud_upload</i>
                                </div>
                                
                                {selectedFile && (
                                    <div className="file-info">
                                        <div className="file-details">
                                            <i className="material-icons file-icon" style={{ color: currentTile.color }}>description</i>
                                            <div>
                                                <strong>{selectedFile.name}</strong>
                                            </div>
                                            <div className="file-status success">
                                                <i className="material-icons">check_circle</i>
                                                Érvényes
                                            </div>
                                            <button 
                                                className="deselect-button" 
                                                onClick={handleDeselectFile}
                                                title="Fájl kiválasztás visszavonása"
                                            >
                                                <i className="material-icons">close</i>
                                            </button>
                                        </div>
                                    </div>
                                )}
                                
                                <div className="upload-actions">
                                    <label className="btn btn-secondary">
                                        <i className="material-icons">folder_open</i>
                                        Fájl kiválasztása
                                        <input 
                                            type="file" 
                                            accept=".xlsx,.xls" 
                                            onChange={(e) => handleFileUpload(e, currentTile.id)}
                                            style={{ display: 'none' }}
                                        />
                                    </label>
                                    
                                    <button 
                                        className="btn btn-primary" 
                                        onClick={handleUpload}
                                        disabled={!selectedFile || uploading}
                                    >
                                        {uploading ? (
                                            <>
                                                <div className="spinner"></div>
                                                Feltöltés...
                                            </>
                                        ) : (
                                            <>
                                                <i className="material-icons">publish</i>
                                                Feltöltés
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderPeriodModal = () => {
        if (!showPeriodModal) return null;

        return (
            <div className="modal-overlay" onClick={closePeriodModal}>
                <div className="modal-content period-modal-content" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-header" style={{ background: 'linear-gradient(135deg, #607D8B 0%, #455A64 100%)' }}>
                        <div className="modal-title">
                            <i className="material-icons">event</i>
                            <div>
                                <h2>Időszakok kezelése</h2>
                            </div>
                        </div>
                        <button className="modal-close-button" onClick={closePeriodModal}>
                            <i className="material-icons">close</i>
                        </button>
                    </div>
                    
                    <div className="modal-body">
                        <div className="period-form-section">
                            <h3>{editingPeriodName ? 'Időszak szerkesztése' : 'Új időszak hozzáadása'}</h3>
                            <div className="period-form">
                                <div className="form-row">
                                    <div className="form-group">
                                        <label htmlFor="name">Félév (pl. 2025/26-1):</label>
                                        <input
                                            type="text"
                                            id="name"
                                            name="name"
                                            value={periodForm.name}
                                            onChange={handlePeriodFormChange}
                                            placeholder="pl. 2025/26-1"
                                            disabled={editingPeriodName}
                                            required
                                        />
                                        {formErrors.name && <span className="field-error">{formErrors.name}</span>}
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label htmlFor="start_date">Kitöltés kezdő dátum:</label>
                                        <input
                                            type="date"
                                            id="start_date"
                                            name="start_date"
                                            value={periodForm.start_date}
                                            onChange={handlePeriodFormChange}
                                            required
                                        />
                                        {formErrors.start_date && <span className="field-error">{formErrors.start_date}</span>}
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="end_date">Kitöltés záró dátum:</label>
                                        <input
                                            type="date"
                                            id="end_date"
                                            name="end_date"
                                            value={periodForm.end_date}
                                            onChange={handlePeriodFormChange}
                                            required
                                        />
                                        {formErrors.end_date && <span className="field-error">{formErrors.end_date}</span>}
                                    </div>
                                </div>
                                <div className="form-group checkbox-group">
                                </div>
                                <div className="form-actions">
                                    <button 
                                        className="btn btn-secondary" 
                                        onClick={resetPeriodForm}
                                        disabled={savingPeriod}
                                    >
                                        <i className="material-icons">clear</i>
                                        Mégse
                                    </button>
                                    <button 
                                        className="btn btn-primary" 
                                        onClick={handleSavePeriod}
                                        disabled={savingPeriod}
                                    >
                                        {savingPeriod ? (
                                            <>
                                                <div className="spinner"></div>
                                                Mentés...
                                            </>
                                        ) : (
                                            <>
                                                <i className="material-icons">{editingPeriodName ? 'save' : 'add'}</i>
                                                {editingPeriodName ? 'Frissítés' : 'Hozzáadás'}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="period-list-section">
                            <h3>Meglévő időszakok</h3>
                            {loadingPeriods ? (
                                <div className="loading-periods">
                                    <div className="spinner"></div>
                                    Betöltés...
                                </div>
                            ) : periods.length === 0 ? (
                                <p className="no-periods">Még nincsenek rögzített időszak.</p>
                            ) : (
                                <div className="periods-table-container">
                                    <table className="periods-table">
                                        <thead>
                                            <tr>
                                                <th>Félév</th>
                                                <th>Kitöltés kezdő dátum</th>
                                                <th>Kitöltés Záró dátum</th>
                                                <th>Státusz</th>
                                                <th>Műveletek</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {periods.map(period => (
                                                <tr key={period.name} className={period.is_active ? 'active-period' : ''}>
                                                    <td><strong>{period.name}</strong></td>
                                                    <td>{formatPeriodDate(period.start_date)}</td>
                                                    <td>{formatPeriodDate(period.end_date)}</td>
                                                    <td>
                                                        {period.is_active ? (
                                                            <span className="status-badge active">Aktív</span>
                                                        ) : (
                                                            <span className="status-badge inactive">Inaktív</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <div className="action-buttons">
                                                            <button
                                                                className="btn-icon edit"
                                                                onClick={() => handleEditPeriod(period)}
                                                                disabled={savingPeriod || deletingPeriodName === period.name}
                                                                title="Szerkesztés"
                                                            >
                                                                <i className="material-icons">edit</i>
                                                            </button>
                                                            <button
                                                                className="btn-icon delete"
                                                                onClick={() => handleDeletePeriod(period.name)}
                                                                disabled={savingPeriod || deletingPeriodName === period.name}
                                                                title="Törlés"
                                                            >
                                                                <i className="material-icons">delete</i>
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="adatmodositas-container">
            {renderNotification()}
            
            <div className="hero-section">
                <div className="hero-content">
                    <h1>Adatmódosítás</h1>
                </div>
            </div>

            <div className="content-section">
                <div className="tiles-grid">
                    {tiles.map((tile) => (
                        <div 
                            key={tile.id}
                            className="data-tile"
                            onClick={() => openModal(tile.id)}
                        >
                            <div className="tile-background" style={{ background: tile.gradient }}></div>
                            <div className="tile-content">
                                <div className="tile-icon" style={{ background: tile.gradient }}>
                                    <i className="material-icons">{tile.icon}</i>
                                </div>
                                <div className="tile-info">
                                    <h3>{tile.title}</h3>
                                    <p>{tile.description}</p>
                                </div>
                                <div className="tile-action">
                                    <i className="material-icons">arrow_forward</i>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="period-management-section">
                    <button 
                        className="btn btn-period-management"
                        onClick={openPeriodModal}
                    >
                        <i className="material-icons">event</i>
                        Időszakok kezelése
                    </button>
                </div>
            </div>

            {renderModal()}
            {renderPeriodModal()}
        </div>
    );
};

export default AdatModositas;
